'use client';

import React, { useMemo } from 'react';

import { StoreContent } from '@/app/store/content';
import { findPrimaryPostBlockIndex } from '@/lib/blockUtils';
import { EBlockType } from '@/lib/enums';
import { IBlock } from '@/lib/types';
import { loginStateService } from '@/state/login.state.service';
import { useObservable } from '@/state/observable';

import { BlockBatch } from './BlockBatch';
import { BLOCKS_PER_BATCH, useBlockBatches } from './hooks/useBlockBatches';

interface IRenderedPageProps {
  blocks?: IBlock[];
  /** The blocks came with the document: its first batch paints as-is, no asset gate first. */
  isServerRendered?: boolean;
}

export const RenderedPage: React.FC<IRenderedPageProps> = ({ blocks, isServerRendered = false }) => {
  const isLoggedIn = Boolean(useObservable(loginStateService.isLoggedIn));
  const { visibleBatches, hasMoreBatches, sentinelRef } = useBlockBatches({
    blocks: blocks ?? [],
  });

  const hasOfferBlocks = useMemo(() => {
    return blocks?.some((block) => block.type === EBlockType.OFFER);
  }, [blocks]);
  const primaryPostBlockIndex = useMemo(() => findPrimaryPostBlockIndex(blocks ?? []), [blocks]);

  if (!blocks || blocks.length === 0) return <></>;

  return (
    <div className="renderedPage flex flex-col gap-5 px-[18px] md:gap-[22px] md:p-0">
      {hasOfferBlocks && isLoggedIn && <StoreContent />}
      {visibleBatches.map((batchBlocks: IBlock[], batchIndex: number) => (
        <BlockBatch
          key={batchIndex}
          blocks={batchBlocks}
          firstBlockIndex={batchIndex * BLOCKS_PER_BATCH}
          skipAssetGate={isServerRendered && batchIndex === 0}
          primaryPostBlockIndex={primaryPostBlockIndex}
        />
      ))}
      {hasMoreBatches && <div ref={sentinelRef} aria-hidden="true" className="h-px w-full" />}
    </div>
  );
};
